import { AxiosResponse } from "axios";
import * as React from "react";
import { ChangeEvent, useEffect, useState } from "react";
import { ProjectService } from "../../../../services/project-service";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaCheckbox from "../../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaLoader from "../../../ui-kit/kuika-loader/kuika-loader";
import KuikaMenuItem from "../../../ui-kit/kuika-menu-item/kuika-menu-item";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import { IWorkspaceRole } from "./role-aggregation";

interface IProjectNameItem {
  id: string;
  name: string;
}

interface IManageRoleProjectsModalProps {
  role: IWorkspaceRole | null;
  showModal: boolean;
  closeModal: () => void;
  onDone: () => void;
}

// KOA-18634: manage which projects a workspace app role belongs to — tick to add, untick to remove.
// Adds reuse the role's canonical id (roleIds[0]); removes delete it from that project's roles[]
// (orphaned permittedRoles are tolerated by code generation, matching the designer's own deletion).
// At least one project must keep the role, so the role never becomes an orphan.
const ManageRoleProjectsModal = (props: IManageRoleProjectsModalProps): JSX.Element | null => {
  const { role, showModal, closeModal, onDone } = props;
  const [allProjects, setAllProjects] = useState<IProjectNameItem[]>([]);
  const [originalIds, setOriginalIds] = useState<string[]>([]);
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect((): (() => void) | void => {
    if (!showModal || role === null) {
      return;
    }
    let cancelled: boolean = false;
    const load = async (): Promise<void> => {
      setIsLoading(true);
      setSearchText("");
      try {
        const response: AxiosResponse<IProjectNameItem[]> = await ProjectService.GetAllProjectNames();
        const projects: IProjectNameItem[] = (response.data ?? [])
          .slice()
          .sort((a: IProjectNameItem, b: IProjectNameItem): number => a.name.localeCompare(b.name));
        const usedProjectIds: string[] = projects
          .map((project: IProjectNameItem): string => project.id)
          .filter((projectId: string): boolean =>
            role.occurrences.some((occurrence): boolean => occurrence.projectId === projectId)
          );
        if (!cancelled) {
          setAllProjects(projects);
          setOriginalIds(usedProjectIds);
          setCheckedIds(usedProjectIds);
        }
      } catch (error) {
        KMainFunctions.exceptionHandler(error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    void load();
    return (): void => {
      cancelled = true;
    };
  }, [showModal, role]);

  const toggle = (projectId: string): void => {
    setCheckedIds((previous: string[]): string[] =>
      previous.includes(projectId)
        ? previous.filter((id: string): boolean => id !== projectId)
        : [...previous, projectId]
    );
  };

  const handleSave = async (): Promise<void> => {
    if (role === null) {
      return;
    }
    const added: string[] = checkedIds.filter((id: string): boolean => !originalIds.includes(id));
    const removed: string[] = originalIds.filter((id: string): boolean => !checkedIds.includes(id));
    if (added.length === 0 && removed.length === 0) {
      closeModal();
      return;
    }
    const canonicalId: string = role.roleIds[0];
    setIsSubmitting(true);
    try {
      for (const projectId of added) {
        await ProjectService.ExtendRoleToProject(projectId, canonicalId, role.name);
      }
      for (const projectId of removed) {
        await ProjectService.RemoveRoleFromProject(projectId, role.name);
      }
      KMainFunctions.displaySuccessNotification(`"${role.name}" project assignments updated.`);
      onDone();
      closeModal();
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (role === null) {
    return null;
  }

  const hasChanges: boolean =
    checkedIds.some((id: string): boolean => !originalIds.includes(id)) ||
    originalIds.some((id: string): boolean => !checkedIds.includes(id));
  const canSubmit: boolean = checkedIds.length >= 1 && hasChanges && !isSubmitting;

  const renderBody = (): React.ReactNode => {
    if (isLoading) {
      return (
        <div className="kuika-manage-role__loading">
          <KuikaLoader />
        </div>
      );
    }
    const needle: string = searchText.trim().toLowerCase();
    const visibleProjects: IProjectNameItem[] =
      needle.length === 0
        ? allProjects
        : allProjects.filter((project: IProjectNameItem): boolean => project.name.toLowerCase().includes(needle));
    return (
      <div className="kuika-manage-role">
        <KuikaInput
          data-testid="manageRoleSearch"
          placeholder="Search projects..."
          leftIcon={<KuikaSvg name="search" width={16} height={16} />}
          rightIcon={
            searchText ? (
              <KuikaSvg name="x" width={16} height={16} pointer onClick={(): void => setSearchText("")} />
            ) : undefined
          }
          value={searchText}
          onChange={(event: ChangeEvent<HTMLInputElement>): void => setSearchText(event.currentTarget.value)}
        />
        <div className="kuika-manage-role__list">
          {visibleProjects.length === 0 ? (
            <div className="kuika-manage-role__empty">
              <KuikaSmallText>No projects match your search.</KuikaSmallText>
            </div>
          ) : (
            visibleProjects.map((project: IProjectNameItem): React.ReactNode => {
              const checked: boolean = checkedIds.includes(project.id);
              return (
                <KuikaMenuItem
                  key={project.id}
                  title={project.name}
                  selected={checked}
                  onClick={(): void => toggle(project.id)}
                  leftIcon={
                    <KuikaCheckbox
                      checked={checked}
                      onChange={(): void => toggle(project.id)}
                      dataTestId={`manageProject-${project.id}`}
                    />
                  }
                />
              );
            })
          )}
        </div>
        <div className="kuika-manage-role__hint">
          <KuikaSmallText>At least one project must keep this role.</KuikaSmallText>
        </div>
      </div>
    );
  };

  return (
    <KModal
      showModal={showModal}
      closeModal={closeModal}
      header={`Manage projects for "${role.name}"`}
      width={600}
      footer={
        <KModalFooter
          submitLabel="Save"
          onSubmit={handleSave}
          onCancel={closeModal}
          loading={isSubmitting}
          disabled={!canSubmit}
        />
      }
    >
      {renderBody()}
    </KModal>
  );
};

export { ManageRoleProjectsModal };
